import Api from '.';
import { endpoints } from './endpoints';
import { adaptPersonalGet } from './personalAdapter';

// Stage 9 — personal-details enum categories whose BE codes (occupation:
// SALARIED…, annualIncomeRange: 1L_TO_5L…, tradingExperience: 1Y_TO_5Y…) do NOT
// match the legacy hardcoded fallback codes in personalAdapter's DROPDOWNS. When
// the BE doesn't inline these option lists on GET /personal-details, the saved
// code can't be matched to a chip and the field renders blank ("not fetched").
// We hydrate them from the canonical GET /lookups/{category} (anonymous) so the
// saved value prefills and the PUT round-trips the right code — exactly how
// marital/education/settlement already work (their hardcoded codes happen to
// match the BE). `ddKey` is the dropDownValues field adaptPersonalGet reads;
// `categories` are candidate lookup names (BE casing isn't fully published, so
// the first non-empty list wins). A miss falls back to the hardcoded list
// (today's behaviour), so this can never break the page.
const LOOKUP_HYDRATION = [
    { ddKey: 'occupation',           categories: ['occupation'] },
    { ddKey: 'annualIncome',         categories: ['income', 'annualIncome', 'annualIncomeRange'] },
    { ddKey: 'investmentExperience', categories: ['tradingExperience', 'investmentExperience', 'trading_experience'] },
];

/**
 * Stage 9 — Personal details.
 *
 * In transition: legacy methods (getPersonalInfo, updateDetails, etc.) target
 * the on-prem backend. The new contract is the `get`/`save` pair against
 * /personal-details. Legacy methods are retired as their callers migrate
 * (Phase B).
 */
export default class PersonalApi extends Api {
    // --- Legacy on-prem contract -------------------------------------------
    // Stage 9 (eKYC 3.0) reroute: dadPersonal/index.js still calls
    // `personalAPI.getPersonalInfo(data).then(...)` directly (line ~796) for
    // the on-mount fetch that drives `setPersonalArrays` / `setPersonalValues`.
    // Legacy `/personalDeatils/get` is gone, so we reroute through the new
    // GET /personal-details and synth the legacy envelope shape so the
    // page's `response.data.Data.PersonalDetails / .DropDownValues /
    // .SELECTEDSEGMENT` destructure stays valid. Phase D removes when the
    // page consumes the canonical PersonalDetailsResult directly.
    async getPersonalInfo(/* data */) {
        const beData = await this.getMergedBeData();
        const { personalDetails, dropDownValues } = adaptPersonalGet(beData || {});
        return {
            data: {
                Status: true,
                Message: '',
                Data: {
                    PersonalDetails: {
                        ...personalDetails,
                        ISKRAADDRESS: '0',
                        Show_Settlement_Cycle: '1',
                    },
                    DropDownValues: dropDownValues,
                    // Alliance prefill: the BE returns an additive
                    // `partnerSupplied: { partnerCode, fields: [...] }` block
                    // (null for a non-alliance lead) naming the Stage 9 fields the
                    // partner bank already sent. dadPersonal disables exactly those
                    // inputs. Passed through verbatim — never branch on partnerCode.
                    PartnerSupplied: beData && beData.partnerSupplied ? beData.partnerSupplied : null,
                    // Alliance nominee DRAFT (partnerNominee). Present only when the bank
                    // sent a nominee AND the customer has none of their own — the BE
                    // suppresses it once a saved nominee exists. Nothing is persisted:
                    // dadPersonal seeds the nominee sub-form with it and the customer
                    // confirms through the normal POST /nominees path. It rides on this
                    // response because GET /nominees returns a bare array.
                    PartnerNominee: beData && beData.partnerNominee ? beData.partnerNominee : null,
                    SELECTEDSEGMENT: [{
                        ISEQUITY: 'N',
                        ISDERIVATIVE: 'N',
                        ISCURRENCY: 'N',
                        ISCOMMODITY: 'N',
                        ISCLIENTCONSENTRECEIVED: 'N',
                    }],
                },
            },
        };
    }
    getIncomeProofDetails(data) {
        return this.fetch(this.buildUrl(endpoints.personalInfo.getIncomeProofDetails), 'POST', JSON.stringify(data));
    }
    updatePersonalInfo(data) {
        return this.fetch(this.buildUrl(endpoints.personalInfo.updateDetails), 'POST', JSON.stringify(data));
    }
    updatePersonalOtherDetails(data) {
        return this.fetch(this.buildUrl(endpoints.personalInfo.updatePersonalOtherDetails), 'POST', JSON.stringify(data));
    }
    getPersonalDetailsUsEquity(data) {
        return this.fetch(this.buildUrl(endpoints.personalInfo.getPersonalDetailsUsEquity), 'POST', JSON.stringify(data));
    }
    updatePersonalDetailsUsEquity(data) {
        return this.fetch(this.buildUrl(endpoints.personalInfo.updatePersonalDetailsUsEquity), 'POST', JSON.stringify(data));
    }
    getFatherSposeDetails(data) {
        return this.fetch(this.buildUrl(endpoints.personalInfo.getFatherSposeDetails), 'POST', JSON.stringify(data));
    }
    getHolderTyperDetails(data) {
        return this.fetch(this.buildUrl(endpoints.personalInfo.getHolderTyperDetails), 'POST', JSON.stringify(data));
    }

    // --- New eKYC 3.0 contract ---------------------------------------------

    /** GET /personal-details (X-User-Id header) */
    get() {
        return this.fetch(
            this.buildUrl(endpoints.stages.personal.details),
            'GET',
            undefined,
            { lead: { mode: 'header', field: 'userId' } },
        );
    }

    /**
     * PUT /personal-details/dp-scheme (X-User-Id header) — the CRM (branch) "DP Scheme"
     * dropdown, saved on every change. `changedAt` is the client stamp of the change; the
     * BE treats it as the ordering key (an older stamp than the stored one is answered
     * with `applied: false` and the stored value, never written), so the last action
     * always wins. Resolves to `{ applied, selectedDpScheme, updatedAt, tariffData }`.
     * The transport's silent 401 refresh re-sends the same body, so the stamp survives.
     */
    saveDpScheme({ dpScheme, changedAt }) {
        return this.fetch(
            this.buildUrl(endpoints.stages.personal.dpScheme),
            'PUT',
            JSON.stringify({ dpScheme, changedAt }),
            { lead: { mode: 'header', field: 'userId' } },
        );
    }

    /**
     * GET /lookups/{category} (anonymous) → [{ code, displayName }].
     * Returns [] on any error so callers can fall back gracefully.
     */
    async lookup(category) {
        try {
            const data = await this.fetch(
                this.buildUrl(endpoints.stages.lookups.category(category)),
                'GET',
                undefined,
                { anonymous: true },
            );
            return Array.isArray(data) ? data : [];
        } catch (e) {
            return [];
        }
    }

    /** Try each candidate category in order; resolve the first non-empty list. */
    async _firstNonEmptyLookup(categories) {
        for (const category of categories) {
            // eslint-disable-next-line no-await-in-loop
            const rows = await this.lookup(category);
            if (rows && rows.length) return rows;
        }
        return [];
    }

    /**
     * GET /personal-details, then hydrate the enum option lists whose BE codes
     * differ from the hardcoded fallbacks (occupation / annual-income /
     * trading-experience) from GET /lookups/{category}. Returns the BE data with
     * a merged `dropDownValues` so adaptPersonalGet renders BE-coded chips that
     * match the saved value and prefill correctly. Categories the BE already
     * inlines are left untouched; lookup failures fall back to the adapter's
     * hardcoded list (today's behaviour), so this never breaks the page.
     */
    async getMergedBeData() {
        const beData = (await this.get()) || {};
        const dd = beData.dropDownValues || {};
        const lists = await Promise.all(
            LOOKUP_HYDRATION.map(({ ddKey, categories }) =>
                (Array.isArray(dd[ddKey]) && dd[ddKey].length)
                    ? Promise.resolve(null) // BE already inlined this list — keep it
                    : this._firstNonEmptyLookup(categories),
            ),
        );
        const hydrated = {};
        LOOKUP_HYDRATION.forEach(({ ddKey }, i) => {
            const rows = lists[i];
            if (rows && rows.length) hydrated[ddKey] = rows;
        });
        return { ...beData, dropDownValues: { ...dd, ...hydrated } };
    }

    /** PUT /personal-details — body field `userId:long` per BE Stage 9 contract */
    save(payload) {
        return this.fetch(
            this.buildUrl(endpoints.stages.personal.details),
            'PUT',
            JSON.stringify(payload),
            { lead: { mode: 'body', field: 'userId' } },
        );
    }
}
